import React, { useEffect, useState } from 'react';
import {
  Grid,
  TextField,
  Button,
  Autocomplete,
  Chip,
  Tooltip,
  useTheme,
  alpha,
} from '@mui/material';
import { Groups as GroupsIcon } from '@mui/icons-material';
import {
  WorkPackage,
  WorkUnit,
  DocumentType,
  Discipline,
  User,
  Project,
} from '../../types';
import projectsService from '../../services/projectsService';
import { getZoomCompensatedAutocompleteSlotProps } from '../../utils/zoomCompensation';
import { wpNumberForNdSuffix, wuIndexWithinPackageForNdSuffix } from '../../utils/ndNumberUtils';
import {
  buildCommunicationListOptions,
  buildCommunicationListValue,
  isProjectMembersOption,
  parseCommunicationListChange,
  CommunicationListOption,
} from '../../utils/communicationList';
import { DocumentFormValues } from './formTypes';

const NAUTILUS = { deepOcean: '#0f2134', sand: '#ccba9b' };

export interface DocumentFormProps {
  value: DocumentFormValues;
  onChange: (value: DocumentFormValues) => void;
  mode: 'create' | 'edit';
  projectId: number;
  workPackages: WorkPackage[];
  workUnits: WorkUnit[];
  documentTypes: DocumentType[];
  disciplines: Discipline[];
  projectUsers: User[];
  currentProject: Project | null;
  onLoadWorkUnits?: (workPackageId: string) => void;
  ndNumberAutoEnabled?: boolean;
  onNdNumberManualEdit?: () => void;
  workUnitRequired?: boolean;
  invalid?: (key: string) => boolean;
  onFieldEdit?: (key: string) => void;
}

const DocumentForm: React.FC<DocumentFormProps> = ({
  value,
  onChange,
  mode,
  projectId,
  workPackages,
  workUnits,
  documentTypes,
  disciplines,
  projectUsers,
  currentProject,
  onLoadWorkUnits,
  ndNumberAutoEnabled = true,
  onNdNumberManualEdit,
  workUnitRequired = true,
  invalid,
  onFieldEdit,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const panelBorder = isDark ? '#2c3e50' : '#e0e0e0';
  const controlBg = isDark ? alpha('#0a1622', 0.6) : alpha(NAUTILUS.deepOcean, 0.03);

  const textFieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 1,
      bgcolor: controlBg,
      '& fieldset': { borderColor: panelBorder },
      '&:hover fieldset': {
        borderColor: isDark ? alpha(NAUTILUS.sand, 0.35) : alpha(NAUTILUS.deepOcean, 0.25),
      },
      '&.Mui-focused fieldset': { borderColor: NAUTILUS.sand },
      '&.Mui-error fieldset': { borderColor: theme.palette.error.main },
      '&.Mui-error:hover fieldset': { borderColor: theme.palette.error.main },
      '&.Mui-error.Mui-focused fieldset': { borderColor: theme.palette.error.main },
    },
  };

  const [suggestedDocId, setSuggestedDocId] = useState<number | null>(null);
  const isNdNumberAuto = mode === 'create' && ndNumberAutoEnabled;

  const patch = (partial: Partial<DocumentFormValues>) => {
    onChange({ ...value, ...partial });
  };

  useEffect(() => {
    if (mode !== 'create' || !projectId) {
      setSuggestedDocId(null);
      return;
    }
    if (!value.discipline_id || !value.document_type_id || !value.work_package_id) {
      setSuggestedDocId(null);
      return;
    }
    if (workUnitRequired && !value.work_unit_id) {
      setSuggestedDocId(null);
      return;
    }

    const fetchSuggestedId = async () => {
      try {
        const nextIdData = await projectsService.getNextDocumentSuggestedId(
          projectId,
          Number(value.discipline_id),
          Number(value.document_type_id),
          Number(value.work_package_id),
          workUnitRequired && value.work_unit_id ? Number(value.work_unit_id) : undefined
        );
        setSuggestedDocId(nextIdData.next_id);
      } catch (err) {
        console.error('Error fetching next document ID:', err);
      }
    };

    void fetchSuggestedId();
  }, [
    mode,
    projectId,
    value.discipline_id,
    value.document_type_id,
    value.work_package_id,
    value.work_unit_id,
    workUnitRequired,
  ]);

  useEffect(() => {
    if (
      mode !== 'create' ||
      !isNdNumberAuto ||
      suggestedDocId == null ||
      !currentProject ||
      !value.work_package_id ||
      (workUnitRequired && !value.work_unit_id)
    ) {
      return;
    }

    const discipline = disciplines.find((d) => d.id === Number(value.discipline_id));
    const docType = documentTypes.find((dt) => dt.id.toString() === value.document_type_id);
    const wp = workPackages.find((w) => w.id.toString() === value.work_package_id);
    const wu = workUnitRequired
      ? workUnits.find((u) => u.id.toString() === value.work_unit_id)
      : undefined;

    const projectNd = currentProject.nd_number || 'ND-XX-YY-00-ZZ';
    const disciplineCode = discipline?.discipline || 'AAA';
    const typeCode = docType?.name || 'BBB';
    const wpCounterStr = wpNumberForNdSuffix(wp?.code, wp?.counter);
    const wuCounterStr = workUnitRequired
      ? wuIndexWithinPackageForNdSuffix(wu?.code)
      : '0';
    const counterStr = suggestedDocId.toString().padStart(3, '0');
    const newNdNumber = `${projectNd}-${disciplineCode}-${typeCode}-${wpCounterStr}${wuCounterStr}${counterStr}`;

    if (value.nd_number !== newNdNumber) {
      patch({ nd_number: newNdNumber });
    }
  }, [
    mode,
    isNdNumberAuto,
    suggestedDocId,
    currentProject,
    value.discipline_id,
    value.document_type_id,
    value.work_package_id,
    value.work_unit_id,
    value.nd_number,
    workUnitRequired,
    disciplines,
    documentTypes,
    workPackages,
    workUnits,
  ]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6}>
        <Autocomplete
          options={documentTypes}
          getOptionLabel={(option) => option.name}
          value={documentTypes.find((t) => t.id.toString() === value.document_type_id) || null}
          onChange={(_, newValue) => {
            onFieldEdit?.('document_type_id');
            patch({ document_type_id: newValue?.id.toString() || '' });
          }}
          slotProps={getZoomCompensatedAutocompleteSlotProps()}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Document Type"
              required
              fullWidth
              error={Boolean(invalid?.('document_type_id'))}
              sx={textFieldSx}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Autocomplete
          options={disciplines}
          getOptionLabel={(option) => `${option.discipline} (${option.description})`}
          value={disciplines.find((d) => d.id.toString() === value.discipline_id) || null}
          onChange={(_, newValue) => {
            onFieldEdit?.('discipline_id');
            patch({ discipline_id: newValue?.id.toString() || '' });
          }}
          slotProps={getZoomCompensatedAutocompleteSlotProps()}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Discipline"
              required
              fullWidth
              error={Boolean(invalid?.('discipline_id'))}
              sx={textFieldSx}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Autocomplete
          options={workPackages}
          getOptionLabel={(option) =>
            `${option.code || option.id.toString()}${option.title ? ` - ${option.title}` : ''}`
          }
          value={workPackages.find((wp) => wp.id.toString() === value.work_package_id) || null}
          onChange={(_, newValue) => {
            const selectedWorkPackageId = newValue?.id.toString() || '';
            onFieldEdit?.('work_package_id');
            patch({ work_package_id: selectedWorkPackageId, work_unit_id: '' });
            onLoadWorkUnits?.(selectedWorkPackageId);
          }}
          slotProps={getZoomCompensatedAutocompleteSlotProps()}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Work Package"
              required
              fullWidth
              error={Boolean(invalid?.('work_package_id'))}
              sx={textFieldSx}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Autocomplete
          options={workUnits}
          getOptionLabel={(option) =>
            `${option.code || option.id.toString()}${option.title ? ` - ${option.title}` : ''}`
          }
          value={workUnits.find((wu) => wu.id.toString() === value.work_unit_id) || null}
          onChange={(_, newValue) => {
            onFieldEdit?.('work_unit_id');
            patch({ work_unit_id: newValue?.id.toString() || '' });
          }}
          disabled={!value.work_package_id || !workUnitRequired}
          slotProps={getZoomCompensatedAutocompleteSlotProps()}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Work Unit"
              required={workUnitRequired}
              fullWidth
              error={Boolean(invalid?.('work_unit_id'))}
              sx={textFieldSx}
              helperText={
                value.work_package_id && !workUnitRequired
                  ? 'No work units configured for this package'
                  : undefined
              }
            />
          )}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="ND Number"
          value={value.nd_number}
          error={Boolean(invalid?.('nd_number'))}
          onChange={(e) => {
            onFieldEdit?.('nd_number');
            patch({ nd_number: e.target.value });
            onNdNumberManualEdit?.();
          }}
          required
          sx={textFieldSx}
          helperText={
            mode === 'create'
              ? isNdNumberAuto
                ? 'Auto-generated suggestion'
                : 'Manually edited'
              : undefined
          }
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Client Number"
          value={value.em_number}
          onChange={(e) => patch({ em_number: e.target.value })}
          sx={textFieldSx}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Title"
          value={value.title}
          error={Boolean(invalid?.('title'))}
          onChange={(e) => {
            onFieldEdit?.('title');
            patch({ title: e.target.value });
          }}
          required
          sx={textFieldSx}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Notes"
          value={value.notes}
          onChange={(e) => patch({ notes: e.target.value })}
          multiline
          rows={3}
          sx={textFieldSx}
        />
      </Grid>
      <Grid item xs={12}>
        <Autocomplete
          multiple
          options={buildCommunicationListOptions(projectUsers)}
          getOptionLabel={(option) =>
            isProjectMembersOption(option)
              ? option.name || 'Project Members'
              : option.name || option.email || String(option.id)
          }
          isOptionEqualToValue={(option, val) => option.id === val.id}
          value={buildCommunicationListValue(
            projectUsers,
            value.communication_include_project_members,
            value.communication_user_ids
          )}
          onChange={(_, newValue) => {
            const parsed = parseCommunicationListChange(newValue as CommunicationListOption[]);
            patch(parsed);
          }}
          slotProps={{
            popper: {
              modifiers: [{ name: 'preventOverflow', options: { boundary: 'window' } }],
              ...getZoomCompensatedAutocompleteSlotProps().popper,
            },
          }}
          renderTags={(tagValue, getTagProps) =>
            tagValue.map((option, index) => {
              const { key, ...tagProps } = getTagProps({ index });
              if (isProjectMembersOption(option)) {
                return (
                  <Tooltip
                    key={key}
                    title="Always reflects the current ND team assigned to this project"
                  >
                    <Chip
                      {...tagProps}
                      icon={<GroupsIcon />}
                      label="Project Members"
                      color="primary"
                      variant="filled"
                    />
                  </Tooltip>
                );
              }
              return (
                <Chip
                  key={key}
                  {...tagProps}
                  label={option.name || option.email || String(option.id)}
                  variant="outlined"
                />
              );
            })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Communication List"
              placeholder="Select users to notify on transmittal"
              variant="outlined"
              sx={textFieldSx}
            />
          )}
        />
      </Grid>
    </Grid>
  );
};

export default DocumentForm;
